<template>
    <aside
        :class="[
            'flex flex-col bg-gradient-to-b from-primary to-primary-dark text-white h-screen w-[250px] fixed top-0 left-0 z-[900] transition-all duration-300 pt-16 shadow-lg',
            isCollapsed ? 'w-20' : 'w-[250px]'
        ]"
    >
        <!-- Toggle Button -->
        <div class="flex justify-end p-4">
            <button
                @click="toggleSidebar"
                class="bg-white/10 hover:bg-white/20 border-0 text-white cursor-pointer w-8 h-8 flex items-center justify-center rounded-full transition-colors"
            >
                <BaseIcon
                    :name="isCollapsed ? 'chevron-right' : 'chevron-left'"
                    size="18"
                    color="white"
                />
            </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 overflow-y-auto pt-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-white/5">
            <ul class="list-none p-0 m-0">
                <template v-for="(item, index) in menuItems" :key="index">
                    <!-- Section Header -->
                    <li v-if="item.type === 'section'" class="my-5 mx-4">
                        <span
                            v-if="!isCollapsed"
                            class="text-[11px] font-semibold tracking-wider text-white/50 uppercase block px-2"
                        >
                            {{ item.title }}
                        </span>
                        <div v-else class="h-px bg-white/20 mx-2"></div>
                    </li>

                    <!-- Menu with Submenu -->
                    <li
                        v-else-if="item.children"
                        :class="[
                            'mx-3 my-1.5 rounded-lg overflow-hidden transition-colors',
                            {
                                'bg-secondary shadow-lg shadow-secondary/40': isActive(item.route) || isParentActive(item.children),
                                'bg-white/5': expandedMenus.includes(index) && !isParentActive(item.children)
                            }
                        ]"
                    >
                        <div
                            @click="toggleSubmenu(index)"
                            class="flex items-center px-4 py-3 cursor-pointer text-white/85 hover:bg-white/10 hover:text-white transition-all rounded-lg relative overflow-hidden group"
                        >
                            <div class="flex items-center justify-center w-6 h-6 mr-4">
                                <BaseIcon :name="item.iconName" size="20" />
                            </div>
                            <span v-if="!isCollapsed" class="flex-1">{{ item.title }}</span>
                            <BaseIcon
                                v-if="!isCollapsed"
                                name="chevron-down"
                                size="16"
                                :class="[
                                    'ml-auto transition-transform duration-300',
                                    expandedMenus.includes(index) ? 'rotate-180' : ''
                                ]"
                            />
                            <div class="absolute inset-0 bg-white/5 -translate-x-full group-hover:translate-x-0 transition-transform duration-300 -z-10"></div>
                        </div>

                        <!-- Submenu -->
                        <transition
                            name="submenu"
                            @enter="onEnter"
                            @leave="onLeave"
                        >
                            <ul
                                v-if="!isCollapsed && expandedMenus.includes(index)"
                                class="list-none p-0 m-0 bg-black/15 rounded-b-lg overflow-hidden"
                            >
                                <li
                                    v-for="(child, childIndex) in item.children"
                                    :key="childIndex"
                                    class="m-0"
                                >
                                    <router-link
                                        :to="child.route"
                                        :class="[
                                            'flex items-center py-2.5 px-4 pl-5 no-underline text-white/75 hover:bg-white/8 hover:text-white transition-all text-sm',
                                            isActive(child.route) ? 'bg-secondary/30 text-secondary font-medium' : ''
                                        ]"
                                    >
                                        <span
                                            :class="[
                                                'w-1.5 h-1.5 rounded-full bg-white/40 mr-3 transition-all',
                                                isActive(child.route) ? 'bg-secondary w-2 h-2' : ''
                                            ]"
                                        ></span>
                                        <span>{{ child.title }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </transition>
                    </li>

                    <!-- Regular Menu Item -->
                    <li
                        v-else
                        :class="[
                            'mx-3 my-1.5 rounded-lg overflow-hidden transition-colors',
                            isActive(item.route) ? 'bg-secondary shadow-lg shadow-secondary/40' : ''
                        ]"
                    >
                        <router-link
                            :to="item.route"
                            class="flex items-center px-4 py-3 no-underline text-white/85 hover:bg-white/10 hover:text-white transition-all rounded-lg relative overflow-hidden group"
                        >
                            <div class="flex items-center justify-center w-6 h-6 mr-4">
                                <BaseIcon :name="item.iconName" size="20" />
                            </div>
                            <span v-if="!isCollapsed">{{ item.title }}</span>
                            <div class="absolute inset-0 bg-white/5 -translate-x-full group-hover:translate-x-0 transition-transform duration-300 -z-10"></div>
                        </router-link>
                    </li>
                </template>
            </ul>
        </nav>

        <!-- Footer Support -->
        <div class="p-5 border-t border-white/10">
            <div
                @click="showSupport"
                class="flex items-center cursor-pointer p-2.5 rounded-lg transition-colors text-white/80 hover:bg-white/10 hover:text-white"
            >
                <div class="flex items-center justify-center w-6 h-6 mr-4">
                    <BaseIcon name="information-circle" size="20" />
                </div>
                <span v-if="!isCollapsed">Support</span>
            </div>
        </div>
    </aside>
</template>

<script>
export default {
    name: 'SideBar',
    data() {
        return {
            isCollapsed: false,
            expandedMenus: [],
            menuItems: [
                // ACCÈS RAPIDE
                {
                    title: 'Tableau de bord',
                    iconName: 'home',
                    route: '/dashboard'
                },
                {
                    title: 'Caisses',
                    iconName: 'shopping-cart',
                    route: '/cash-registers'
                },

                // SECTION VENTES
                {
                    type: 'section',
                    title: 'VENTES & TRANSACTIONS'
                },
                {
                    title: 'Ventes',
                    iconName: 'cash',
                    children: [
                        {
                            title: 'Historique des ventes',
                            route: '/sales'
                        },
                        {
                            title: 'Historique des caisses',
                            route: '/cash-registers-history'
                        }
                    ]
                },
                {
                    title: 'Clients',
                    iconName: 'users',
                    route: '/customers'
                },

                // SECTION STOCK
                {
                    type: 'section',
                    title: 'STOCK & CATALOGUE'
                },
                {
                    title: 'Produits',
                    iconName: 'tag',
                    route: '/products'
                },
                {
                    title: 'Catégories',
                    iconName: 'folder',
                    route: '/categories'
                },
                {
                    title: 'Retraits de stock',
                    iconName: 'download',
                    route: '/stock-withdrawals'
                },
                {
                    title: 'Inventaire',
                    iconName: 'document',
                    children: [
                        {
                            title: 'Nouvel inventaire',
                            route: '/inventory/new'
                        },
                        {
                            title: 'Historique inventaires',
                            route: '/inventory/physical'
                        },
                        {
                            title: 'État du stock',
                            route: '/inventory/overview'
                        },
                        {
                            title: 'Régularisations',
                            route: '/product-regularizations'
                        }
                    ]
                },

                // SECTION ACHATS
                {
                    type: 'section',
                    title: 'ACHATS & APPROVISIONNEMENT'
                },
                {
                    title: 'Fournisseurs',
                    iconName: 'truck',
                    route: '/suppliers'
                },
                {
                    title: 'Commandes',
                    iconName: 'receipt',
                    route: '/purchase-orders'
                },
                {
                    title: 'Factures',
                    iconName: 'badge-dollar',
                    route: '/supplier-invoices'
                },

                // SECTION ADMINISTRATION
                {
                    type: 'section',
                    title: 'ADMINISTRATION'
                },
                {
                    title: 'Utilisateurs',
                    iconName: 'users',
                    route: '/users'
                },
                {
                    title: 'Notifications',
                    iconName: 'bell',
                    route: '/notifications'
                },
                {
                    title: 'Paramètres',
                    iconName: 'cog',
                    route: '/settings'
                }
            ]
        }
    },
    methods: {
        toggleSidebar() {
            this.isCollapsed = !this.isCollapsed;
            if (this.isCollapsed) {
                this.expandedMenus = [];
            }
            this.$emit('sidebar-toggle', this.isCollapsed);
        },
        toggleSubmenu(index) {
            if (this.isCollapsed) {
                return;
            }
            const menuIndex = this.expandedMenus.indexOf(index);
            if (menuIndex > -1) {
                this.expandedMenus.splice(menuIndex, 1);
            } else {
                this.expandedMenus.push(index);
            }
        },
        isActive(route) {
            if (!route) return false;
            return this.$route.path === route || this.$route.path.startsWith(`${route}/`);
        },
        isParentActive(children) {
            if (!children) return false;
            return children.some(child => this.isActive(child.route));
        },
        showSupport() {
            alert('Support technique: support@sellhub.com');
        },
        // Transition methods
        onEnter(el) {
            el.style.maxHeight = '0';
            el.style.opacity = '0';
            requestAnimationFrame(() => {
                el.style.transition = 'all 0.3s ease';
                el.style.maxHeight = el.scrollHeight + 'px';
                el.style.opacity = '1';
            });
        },
        onLeave(el) {
            el.style.maxHeight = el.scrollHeight + 'px';
            el.style.opacity = '1';
            requestAnimationFrame(() => {
                el.style.transition = 'all 0.3s ease';
                el.style.maxHeight = '0';
                el.style.opacity = '0';
            });
        }
    },
    mounted() {
        // Ouvrir automatiquement le sous-menu actif
        this.menuItems.forEach((item, index) => {
            if (item.children && this.isParentActive(item.children)) {
                this.expandedMenus.push(index);
            }
        });
    },
    watch: {
        // Ouvrir le sous-menu quand on navigue vers une de ses pages
        '$route'() {
            if (!this.isCollapsed) {
                this.menuItems.forEach((item, index) => {
                    if (item.children && this.isParentActive(item.children)) {
                        if (!this.expandedMenus.includes(index)) {
                            this.expandedMenus.push(index);
                        }
                    }
                });
            }
        }
    }
}
</script>

<style scoped>
/* Custom scrollbar for webkit browsers */
.scrollbar-thin::-webkit-scrollbar {
    width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
    @apply bg-white/5 rounded-xl;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    @apply bg-white/20 rounded-xl hover:bg-white/30;
}

/* Submenu transition helpers */
.submenu-enter-active,
.submenu-leave-active {
    transition: all 0.3s ease;
}

.submenu-enter-from,
.submenu-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
